import { NgIf, NgFor, AsyncPipe } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  ViewChild,
  inject,
} from '@angular/core';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatSelect, MatSelectChange, MatOption } from '@angular/material/select';
import { MatToolbar, MatToolbarRow } from '@angular/material/toolbar';
import { Router, RouterLink } from '@angular/router';
import { RootState } from '@next/core-lib/app';
import { NotificationEmptyComponent } from '@next/core-lib/components';
import {
  AgrCurrencyPipe,
  AgrNumberPipe,
  LocaleService,
  TranslatePipe,
  AgrDatePipe,
} from '@next/core-lib/i18n';
import { Store, select } from '@ngrx/store';
import { Observable, Subscription, combineLatest } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { ProductTypeEnum } from 'src/app/delivery/models/delivery.model';
import { FeedAdvice } from 'src/app/feed-advice/models/feed-advice.models';
import { getFeedAdvices } from 'src/app/feed-advice/store/feed-advice.selectors';
import { ReferenceValueSheet } from 'src/app/reference-value-sheet/models/reference-value-sheet.model';
import { getReferenceValueSheets } from 'src/app/reference-value-sheet/store/reference-value-sheet.selectors';
import { SettingsServiceHelper } from 'src/app/shared/services/settingsHelperService';

import {
  getBarns,
  getObservationSwitchSetting,
} from '../../../settings/store/selectors/setting.selector';
import { PoultryContextKeys } from '../../../shared/constants';
import {
  BalanceTableRow,
  Barn,
  Cycle,
  FeedConsumptionPerProductType,
  FeedConsumptionSummary,
} from '../../models/cycle.model';
import { CycleService } from '../../services/cycle.service';
import { DiscountNavigationService } from '../../services/discount-navigation.service';
import * as cycleActions from '../../store/actions/cycle.actions';
import { getCycles, getSelectedCycle } from '../../store/selectors/cycle.selectors';

import { CycleOverviewDetailedPanelComponent } from '../cycle-overview-detailed-panel/cycle-overview-detailed-panel.component';

@Component({
  selector: 'app-cycle-overview-detailed',
  templateUrl: 'cycle-overview-detailed.component.html',
  styleUrls: ['cycle-overview-detailed.component.scss'],
  providers: [AgrCurrencyPipe, AgrNumberPipe],
  imports: [
    MatToolbar,
    MatToolbarRow,
    NgIf,
    MatLabel,
    MatSelect,
    ReactiveFormsModule,
    FormsModule,
    NgFor,
    MatOption,
    MatButton,
    MatIcon,
    CycleOverviewDetailedPanelComponent,
    RouterLink,
    NotificationEmptyComponent,
    AsyncPipe,
    TranslatePipe,
    AgrDatePipe,
    AgrCurrencyPipe,
    AgrNumberPipe,
  ],
})
export class CycleOverviewDetailedComponent implements OnDestroy {
  private readonly store = inject<Store<RootState>>(Store);
  private router = inject(Router);
  private readonly locale = inject(LocaleService);
  private currencyPipe = inject(AgrCurrencyPipe);
  private cycleService = inject(CycleService);
  private settingsHelper = inject(SettingsServiceHelper);
  private decimalPipe = inject(AgrNumberPipe);
  private discountNavigationService = inject(DiscountNavigationService);

  @ViewChild('barnSelector') barnSelector: MatSelect;
  @Input() isMobile: boolean;
  @Output() deleteClicked: EventEmitter<Cycle> = new EventEmitter<Cycle>();
  @Output() editClicked: EventEmitter<Cycle> = new EventEmitter<Cycle>();

  lsUserSelectedCycle$ = this.settingsHelper.getSingle<number>(
    0,
    PoultryContextKeys.CYCLES_SELECTED_CYCLE_ID,
  );
  lsUserSelectedBarn$ = this.settingsHelper.getSingle<number>(
    0,
    PoultryContextKeys.CYCLES_SELECTED_BARN_ID,
  );

  canNavigate$: Observable<boolean> = this.discountNavigationService.canNavigate();

  cycles$: Observable<Cycle[]> = combineLatest([
    this.store.select(getCycles),
    this.store.select(getSelectedCycle),
    this.lsUserSelectedCycle$,
  ]).pipe(
    map(([cycles, selectedCycle, lsUserSelectedCycle]: [Cycle[], Cycle, number]) => {
      if (!!selectedCycle && selectedCycle?.id > 0) {
        this.dispatchSelectedCycleId(selectedCycle.id);
      } else if (!!cycles && cycles.length > 0) {
        if (lsUserSelectedCycle === 0) {
          const cycle = cycles.reduce((prev, current) => (prev.id > current.id ? prev : current));
          this.dispatchSelectedCycleId(cycle.id);
        } else {
          const foundCycle = cycles.find((c) => c.id === lsUserSelectedCycle);
          if (foundCycle) {
            this.dispatchSelectedCycleId(foundCycle.id);
          } else {
            this.settingsHelper.removeSettings(PoultryContextKeys.CYCLES_SELECTED_CYCLE_ID);
            this.settingsHelper.removeSettings(PoultryContextKeys.CYCLES_SELECTED_BARN_ID);
            const cycle = cycles.reduce((prev, current) => (prev.id > current.id ? prev : current));
            this.dispatchSelectedCycleId(cycle.id);
          }
        }
      }
      return cycles.sort((a, b) => (a.cycleNumber < b.cycleNumber ? 1 : -1));
    }),
  );

  barns$: Observable<Barn[]> = this.store.select(getBarns);
  selectedCycle: Cycle;
  selectedCycleId: number;
  unfilteredCycle: Cycle;
  barnOptions: Barn[];
  selectedBarn: Barn;
  selectedBarnId: number | string = 'all';
  dayOldChickTotals: Array<{ title: string; value: number | string; col_width: number }>;
  hasDayOldChicks: boolean;
  departureTotals: Array<{ title: string; value: number | string; col_width: number }>;
  hasDepartures: boolean;
  hasBalanceRows: boolean;
  hasDiscounts: boolean;
  balanceRows: BalanceTableRow[];
  feedConsumptionTotals: Array<{ title: string; value: number | string; col_width: number }>;
  feedConsumptionSummary: FeedConsumptionSummary = undefined;
  subscription: Subscription;
  private barnSubscription: Subscription;
  refValSheets$: Observable<ReferenceValueSheet[]> = this.store.select(getReferenceValueSheets);
  feedAdvices$: Observable<FeedAdvice[]> = this.store.select(getFeedAdvices);

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {
    this.subscription = this.store.pipe(select(getSelectedCycle)).subscribe((selectedCycle) => {
      if (selectedCycle !== undefined) {
        this.selectCycle(selectedCycle);
      }
    });
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    if (this.barnSubscription) {
      this.barnSubscription.unsubscribe();
    }
  }

  shouldShowWDOsettings(): Observable<boolean> {
    return this.store.select(getObservationSwitchSetting).pipe(take(1));
  }

  changeCycleSelection(selection: MatSelectChange) {
    this.dispatchSelectedCycleId(selection.value as number);
  }

  private selectCycle(cycle: Cycle) {
    this.selectedCycle = {
      ...cycle,
      // Sort arrivals.
      dayOldChicks: cycle.dayOldChicks
        ?.slice()
        .sort((a, b) => (new Date(b.arrivalDate) < new Date(a.arrivalDate) ? 1 : -1)),
      // Sort departures.
      departures: cycle.departures
        ?.slice()
        .sort((a, b) => (new Date(b.departureDate) < new Date(a.departureDate) ? 1 : -1)),
      // Discounts sorted.
      discounts: cycle.discounts
        ?.slice()
        .sort((a, b) => (b.applicabilityType < a.applicabilityType ? 1 : -1)),
    };

    this.selectedCycleId = cycle.id;
    this.unfilteredCycle = { ...this.selectedCycle };
    this.barnOptions = [];
    this.selectedBarnId = 'all';
    this.populateBarnOptions();

    if (this.barnSubscription) {
      this.barnSubscription.unsubscribe();
    }
    this.settingsHelper.removeSettings(PoultryContextKeys.CYCLES_SELECTED_BARN_ID);

    this.barnSubscription = this.lsUserSelectedBarn$.subscribe((lsSelectedBarnId) => {
      if (lsSelectedBarnId > 0) {
        this.changeBarnSelection(lsSelectedBarnId);
      } else {
        this.calculatePanels();
      }
    });
  }

  changeBarnSelection(barnId: number | string) {
    this.barns$.pipe(take(1)).subscribe((barns) => {
      const foundBarn = barns.find((barn) => barn.id === Number(barnId));
      if (foundBarn) {
        this.selectedBarn = foundBarn;
        this.selectedBarnId = foundBarn.id;
        this.settingsHelper.setSettings(PoultryContextKeys.CYCLES_SELECTED_BARN_ID, foundBarn.id);
      } else {
        this.settingsHelper.removeSettings(PoultryContextKeys.CYCLES_SELECTED_BARN_ID);
        this.selectedBarn = null;
        this.selectedBarnId = 'all';
      }
      this.filterCycleByBarn();
      this.calculatePanels();
    });
  }

  private populateBarnOptions() {
    this.barns$.pipe(take(1)).subscribe((barns) => {
      this.barnOptions = [];
      if (this.selectedCycle?.id) {
        const hasDayOldChicks = this.selectedCycle.dayOldChicks?.length > 0;
        const hasDepartures = this.selectedCycle.departures?.length > 0;
        const hasBalanceRows = this.selectedCycle.balancesPerDeliveryLine?.length > 0;
        const hasDiscounts = this.selectedCycle.discounts?.length > 0;

        barns.forEach((barn) => {
          const barnInArrival =
            hasDayOldChicks &&
            this.selectedCycle.dayOldChicks.findIndex(
              (dayoldchick) => dayoldchick.barn.id === barn.id,
            ) !== -1;
          const barnInDeparture =
            hasDepartures &&
            this.selectedCycle.departures.findIndex(
              (departure) => departure.barn.id === barn.id,
            ) !== -1;
          const barnInBalance =
            hasBalanceRows &&
            this.selectedCycle.balancesPerDeliveryLine.length > 0 &&
            this.selectedCycle.balancesPerDeliveryLine.map((balancerow) =>
              balancerow.barnBalances.findIndex((row) => row.barnId === barn.id),
            );
          const barnInDiscount =
            hasDiscounts &&
            this.selectedCycle.discounts?.findIndex((discount) => discount.barn.id === barn.id) !==
              -1;

          if (
            (hasDayOldChicks && barnInArrival) ||
            (hasDepartures && barnInDeparture) ||
            (hasDiscounts && barnInDiscount) ||
            (barnInBalance.length > 0 && barnInBalance.findIndex((i) => i > -1) !== -1)
          ) {
            this.barnOptions.push(barn);
          }
        });
      }
    });
  }

  private filterCycleByBarn() {
    if (this.selectedBarnId === 'all') {
      this.selectedCycle = this.unfilteredCycle;
    } else {
      this.selectedCycle = <Cycle>{
        ...this.unfilteredCycle,
        dayOldChicks: this.unfilteredCycle.dayOldChicks?.filter(
          (dayoldchick) => dayoldchick.barn?.id === this.selectedBarn.id,
        ),
        departures: this.unfilteredCycle.departures?.filter(
          (departure) => departure.barn?.id === this.selectedBarn.id,
        ),
        balanceRows: this.unfilteredCycle.balancesPerDeliveryLine?.map((balancerow) => ({
          ...balancerow,
          barnBalanceRows: balancerow.barnBalances.filter(
            (row) => row.barnId === this.selectedBarn.id,
          ),
        })),
        discounts: this.unfilteredCycle.discounts?.filter(
          (discount) => discount.barn?.id === this.selectedBarn.id,
        ),
      };
    }
  }

  private calculatePanels() {
    this.hasDayOldChicks =
      !!this.selectedCycle.dayOldChicks && this.selectedCycle.dayOldChicks.length > 0;
    this.hasDepartures =
      !!this.selectedCycle.departures && this.selectedCycle.departures.length > 0;
    this.hasDiscounts = !!this.selectedCycle.discounts && this.selectedCycle.discounts.length > 0;

    if (this.hasDayOldChicks) {
      this.dayOldChickTotals = [
        {
          title: this.locale.translate('cycle-overview.panel.amount'),
          value: this.selectedCycle.dayOldChicks.reduce(
            (amount, dayOldChick) => amount + dayOldChick.amountOfChicks,
            0,
          ),
          col_width: 60,
        },
        {
          title: this.locale.translate('cycle-overview.panel.price-ex-vat'),
          value: this.currencyPipe.transform(
            this.selectedCycle.dayOldChicks.reduce(
              (amount, dayOldChick) =>
                amount + dayOldChick.amountOfChicks * Number(dayOldChick.chicksPrice),
              0,
            ),
            this.selectedCycle.currency,
          ),
          col_width: 140,
        },
      ];
    }
    if (this.hasDepartures) {
      this.departureTotals = [
        {
          title: this.locale.translate('cycle-overview.panel.amount'),
          value: this.selectedCycle.departures.reduce(
            (amount, departure) => amount + Number(departure.amountOfChicks),
            0,
          ),
          col_width: 60,
        },
        {
          title: this.locale.translate('cycle-overview.panel.price-ex-vat'),
          value: this.currencyPipe.transform(
            this.selectedCycle.departures.reduce(
              (amount, departure) => amount + Number(departure.revenue),
              0,
            ),
            this.selectedCycle.currency,
          ),
          col_width: 140,
        },
        {
          title: this.locale.translate('cycle-overview.panel.departureweight'),
          value: this.selectedCycle.departures.reduce(
            (amount, departure) => amount + departure.departureWeight,
            0,
          ),
          col_width: 110,
        },
        {
          title: this.locale.translate('cycle-overview.panel.slaughterweight'),
          value: this.selectedCycle.departures.reduce(
            (amount, departure) => amount + departure.slaughterWeight,
            0,
          ),
          col_width: 110,
        },
      ];
    }

    this.calculateFeedConsumption();

    if (this.feedConsumptionSummary !== undefined) {
      this.feedConsumptionTotals = [
        {
          title: this.locale.translate('cycle-overview.panel.total-consumption'),
          value:
            this.decimalPipe.transform(Math.round(this.feedConsumptionSummary.totalConsumption)) +
            ' kg',
          col_width: 140,
        },
        {
          title: this.locale.translate('cycle-overview.panel.consumption-per-chick'),
          value:
            this.decimalPipe.transform(
              Math.round(this.feedConsumptionSummary.totalConsumptionPerDeliveredChick),
            ) + ' g',
          col_width: 140,
        },
        {
          title: this.locale.translate('cycle-overview.panel.price-ex-vat'),
          value: this.currencyPipe.transform(
            this.feedConsumptionSummary.totalPrice,
            this.selectedCycle.currency,
          ),
          col_width: 140,
        },
      ];
    }
  }

  dispatchSelectedCycleId(cycleId: number) {
    this.store.dispatch(cycleActions.setSelectedCycle({ cycleId }));
  }

  calculateFeedConsumption() {
    let totalChicks = 0;
    if (
      this.selectedCycle !== undefined &&
      this.selectedCycle.balancesPerDeliveryLine?.length > 0
    ) {
      this.feedConsumptionSummary = {
        consumptionPerProductType: [],
        totalConsumption: 0,
        totalConsumptionPerDeliveredChick: 0,
        totalPrice: 0,
      };

      totalChicks = this.unfilteredCycle.dayOldChicks.reduce(
        (amount, dayOldChick) => amount + dayOldChick.amountOfChicks,
        0,
      );
    } else {
      this.feedConsumptionSummary = undefined;
      this.feedConsumptionTotals = undefined;
      return;
    }

    const balanceRows = this.cycleService.transformBalanceRows(
      this.selectedCycle.balancesPerDeliveryLine,
      this.selectedCycle,
    );
    this.selectedCycle.balancesPerDeliveryLine.forEach((balanceRow) => {
      let consumptionPerType = this.feedConsumptionSummary.consumptionPerProductType.find(
        (c) => c.productType === balanceRow.productType,
      );
      const br = balanceRows.find(
        (c) => c.balanceRowId === `${balanceRow.deliveryId}_${balanceRow.deliveryLineIdentifier}`,
      );
      if (br) {
        const balanceRowConsumption = br.quantity;
        const totalPOK = this.calculatePOK(br);
        const perProductType: FeedConsumptionPerProductType = {
          productType: balanceRow.productType,
          consumption: balanceRowConsumption,
          consumptionPerDeliveredChick: Math.round(totalPOK),
          price: br.unitNettoPrice * balanceRowConsumption,
        };
        if (consumptionPerType === undefined) {
          consumptionPerType = perProductType;
          this.feedConsumptionSummary.consumptionPerProductType.push(consumptionPerType);
        } else {
          this.feedConsumptionSummary.totalPrice += perProductType.price;
          consumptionPerType.consumptionPerDeliveredChick +=
            perProductType.consumptionPerDeliveredChick;
          consumptionPerType.price += perProductType.price;
        }
      }
    });

    this.feedConsumptionSummary.consumptionPerProductType.forEach((consumption) => {
      consumption.consumption = 0;
      consumption.consumptionPerDeliveredChick = 0;
      consumption.price = 0;
      if (this.selectedBarnId !== 'all') {
        this.selectedCycle.balancesPerDeliveryLine.forEach((balancePerDeliveryLine) => {
          if (balancePerDeliveryLine.productType === consumption.productType) {
            const barnBalance = balancePerDeliveryLine.barnBalances.find(
              (x) => x.barnId === this.selectedBarnId,
            );

            if (barnBalance) {
              const amountOfChicksByBarn = this.selectedCycle.dayOldChicks.reduce(
                (amount, dayOldChick) => amount + dayOldChick.amountOfChicks,
                0,
              );
              consumption.consumption += barnBalance.amount;
              consumption.consumptionPerDeliveredChick +=
                (barnBalance.amount / amountOfChicksByBarn) * 1000; // because kilogramms to gramms
              consumption.price += balancePerDeliveryLine.unitNettoPrice * barnBalance.amount;
              this.feedConsumptionSummary.totalConsumption += consumption.consumption;
            }
          }
        });
      } else {
        this.selectedCycle.balancesPerDeliveryLine.forEach((balancePerDeliveryLine) => {
          if (balancePerDeliveryLine.productType === consumption.productType) {
            const totalAmount = balancePerDeliveryLine.barnBalances.reduce(
              (total, balance) => total + balance.amount,
              0,
            );
            consumption.consumption += totalAmount;
            consumption.consumptionPerDeliveredChick += (totalAmount / totalChicks) * 1000; // because kilogramms to gramms
            consumption.price += balancePerDeliveryLine.unitNettoPrice * totalAmount;
            this.feedConsumptionSummary.totalConsumption += consumption.consumption;
          }
        });
      }
    });

    this.feedConsumptionSummary.totalConsumption = 0;
    this.feedConsumptionSummary.totalConsumptionPerDeliveredChick = 0;
    this.feedConsumptionSummary.totalPrice = 0;
    this.feedConsumptionSummary.consumptionPerProductType.forEach((consumption) => {
      this.feedConsumptionSummary.totalConsumption += consumption.consumption;
      this.feedConsumptionSummary.totalConsumptionPerDeliveredChick +=
        consumption.consumptionPerDeliveredChick;
      this.feedConsumptionSummary.totalPrice += consumption.price;
    });

    this.feedConsumptionSummary.consumptionPerProductType.sort((a) =>
      a.productType === ProductTypeEnum.MIXEDPRODUCT ? -1 : 1,
    );
  }

  calculatePOK(balanceRow: BalanceTableRow): number {
    const totalAmountOfChicks = this.selectedCycle.departures.reduce(
      (total, departure) => total + departure.amountOfChicks,
      0,
    );
    return totalAmountOfChicks > 0
      ? ((balanceRow.quantity - balanceRow.remainder) * 1000) / totalAmountOfChicks
      : 0;
  }

  addDayOldChick() {
    this.router.navigate(['/cycle', this.selectedCycle.id, 'day-old-chick', 'add']);
  }

  deleteCycle() {
    this.deleteClicked.emit(this.selectedCycle);
  }

  editCycle() {
    this.editClicked.emit(this.selectedCycle);
  }

  isWeekRegistrationsEnabled(cycle: Cycle): boolean {
    return !cycle.cycleDate || cycle.dayOldChicks.length === 0;
  }

  getSurfaceArea(cycle: Cycle): number {
    let surfaceArea = 0;

    if (this.selectedBarnId === 'all') {
      const calculatedBarns: number[] = [];
      cycle.dayOldChicks.forEach((dayOldChick) => {
        if (!calculatedBarns.includes(dayOldChick.barn.id)) {
          surfaceArea += dayOldChick.barn.surfaceArea;
          calculatedBarns.push(dayOldChick.barn.id);
        }
      });
    } else {
      const dayOldChick = cycle.dayOldChicks.find((x) => x.barn.id === this.selectedBarnId);
      surfaceArea += dayOldChick.barn.surfaceArea;
    }

    return surfaceArea;
  }

  getFeedAdviceName(cycle: Cycle): Observable<string> {
    if (cycle.feedAdviceId !== null) {
      return this.feedAdvices$.pipe(
        take(1),
        map((x) => x.find((a) => a.id === cycle.feedAdviceId)?.name),
      );
    }
  }

  getRefValSheetName(cycle: Cycle): Observable<string> {
    if (cycle.referenceValueSheetId !== null) {
      return this.refValSheets$.pipe(
        take(1),
        map((x) => x.find((s) => s.id === cycle.referenceValueSheetId)?.name),
      );
    }
  }
}
